import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Gauge, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useApp();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resetUrl, setResetUrl] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "signup") {
        const { data } = await api.post("/auth/signup", form);
        await login(data.token, data.user);
        navigate("/onboarding");
      } else if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        const state = await login(data.token, data.user);
        navigate(state.ws && state.site ? "/dashboard" : "/onboarding");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setDone(true);
        if (data.reset_url) setResetUrl(data.reset_url);
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token") || "", password: form.password });
        toast.success("Password updated. Please log in.");
        navigate("/login");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const titles = { login: "Log in", signup: "Create your account", forgot: "Reset your password", reset: "Choose a new password" };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex h-14 items-center border-b px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          <span className="text-sm font-bold tracking-tight">SIX9 SEO</span>
        </Link>
      </header>
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">{titles[mode]}</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="auth-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {done && mode === "forgot" ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Email delivery is not configured on this deployment, so your reset link is shown here directly (valid 1 hour):
                </p>
                {resetUrl ? (
                  <a href={resetUrl} className="block break-all rounded-md border bg-muted/40 p-3 font-mono text-xs text-primary" data-testid="reset-link">{resetUrl}</a>
                ) : (
                  <p className="text-sm">If an account exists for that email, a reset link was generated.</p>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {mode === "signup" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" data-testid="auth-name-input" value={form.name} onChange={set("name")} required maxLength={120} />
                  </div>
                )}
                {mode !== "reset" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid="auth-email-input" value={form.email} onChange={set("email")} required />
                  </div>
                )}
                {mode !== "forgot" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="password">{mode === "reset" ? "New password" : "Password"}</Label>
                    <Input id="password" type="password" data-testid="auth-password-input" value={form.password} onChange={set("password")} required minLength={8} />
                    {(mode === "signup" || mode === "reset") && <p className="text-xs text-muted-foreground">At least 8 characters.</p>}
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={busy} data-testid="auth-submit-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {titles[mode]}
                </Button>
              </form>
            )}
            <div className="mt-4 space-y-1 text-center text-xs text-muted-foreground">
              {mode === "login" && (
                <>
                  <p>No account? <Link className="text-primary" to="/signup" data-testid="auth-goto-signup">Sign up</Link></p>
                  <p><Link className="text-primary" to="/forgot-password" data-testid="auth-goto-forgot">Forgot password?</Link></p>
                </>
              )}
              {mode === "signup" && <p>Already have an account? <Link className="text-primary" to="/login" data-testid="auth-goto-login">Log in</Link></p>}
              {(mode === "forgot" || mode === "reset") && <p><Link className="text-primary" to="/login">Back to log in</Link></p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
